import { beneficiariesAvailable } from '@strix/checkout/helpers/beneficiaries'
import types from './types'
import { getAge } from '@/utils/formValidators/ageBetween'
import { isGenderSpecified } from '~/helpers/gender'
import expiringStorage from '~/utils/expiringStorage'
import camelCaseKeysToUnderscore from '~/utils/stringTransform/camelCaseKeysToUnderscore'

export const actions = {
  setBeneficiary ({ commit }, payload) {
    commit(types.SET_BENEFICIARIES, payload)
  },

  clearBeneficiaries ({ commit }) {
    commit(types.CLEAR_BENEFICIARIES)
  },

  addBeneficiary ({ commit, dispatch }, payload) {
    commit(types.ADD_BENEFICIARY, payload)

    dispatch('saveBeneficiaryToStorage')
  },

  editBeneficiary ({ commit, dispatch }, payload) {
    commit(types.EDIT_BENEFICIARY, payload)

    dispatch('saveBeneficiaryToStorage')
  },

  saveBeneficiaryToStorage ({ state, rootState }) {
    if (!rootState.user.isLogged) {
      expiringStorage.set('cart-beneficiaries', state.beneficiaries, 43200) // 30 dni
    }
  },

  clearQuote ({ commit }) {
    commit(types.CLEAR_QUOTE)
  },

  setQuoteFromCartItems ({ state, commit, rootGetters }) {
    const items = rootGetters['checkout/getBeneficiaryItems']

    const quote = items.reduce((acc, item) => {
      acc[item.uid] = {
        beneficiary: [
          ...state.quote[item.uid]?.beneficiary || [],
          ...item.sourceItemUids?.flatMap(uid => state.quote[uid]?.beneficiary || []) || [],
        ],
        type: item._typename,
      }

      const group = item.additionalOptions?.find(option => option.label === 'grouping_number')

      if (group) {
        acc[item.uid].group = group.value
      }

      return acc
    }, {})

    commit(types.SET_QUOTE, quote)
  },

  async setQuoteForPurchaser ({ dispatch, rootGetters }) {
    const items = rootGetters['checkout/getBeneficiaryItems']

    items.forEach((item) => {
      dispatch('upsertQuote', {
        item,
        beneficiaryIndex: 0,
      })
    })

    await dispatch('uploadBeneficiary')
  },

  upsertQuote ({ state, commit, getters }, payload) {
    const { item, beneficiaryIndex } = payload
    const beneficiary = state.beneficiaries[beneficiaryIndex]
    const {
      beneficiaryAgeFrom: ageFrom,
      beneficiaryAgeTo: ageTo,
      beneficiaryGender: gender,
    } = item
    const age = parseInt(getAge(beneficiary.dateOfBirth))

    if (!beneficiariesAvailable(item)) {
      return
    }

    if (isGenderSpecified(gender) && gender !== beneficiary.gender) {
      return
    }

    if ((ageFrom > 0 && age < ageFrom) || (ageTo > 0 && age > ageTo)) {
      return
    }

    if (getters.isDuplicatedBeneficiary(beneficiaryIndex, item.uid)) {
      return
    }

    commit(types.UPSERT_QUOTE, payload)
  },

  async uploadBeneficiary ({ dispatch, getters }) {
    const cartId = this.$cart.cartId()

    dispatch('checkout/setCartLoaded', false, { root: true })

    await this.$graphql({
      requestPayload: {
        mutation: (await import('../../graphql/mutations/setQuoteBeneficiaries.graphql')).default,
        variables: {
          cartId,
          input: {
            items: camelCaseKeysToUnderscore(getters.getQuoteForMutation),
          },
        },
      },
      successHandler: async ({ data: { setQuoteBeneficiaries } }) => {
        await dispatch('checkout/updateCart', setQuoteBeneficiaries, { root: true })
      },
    })

    dispatch('checkout/setCartLoaded', true, { root: true })
  },

  async removeQuote ({ commit, dispatch }, payload) {
    commit(types.REMOVE_QUOTE, payload)
    await dispatch('uploadBeneficiary')
  },

  async getAuthedBeneficiaries ({ commit, dispatch }) {
    await this.$graphql({
      requestPayload: {
        fetchPolicy: 'network-only',
        query: (await import('~/graphql/customer/queries/customerBeneficiaries.graphql')).default,
      },
      successHandler: ({ data: { customerBeneficiaries: { items } } }) => {
        items.forEach(({ personalData: { _typename, ...beneficiary } }) => {
          const isOtherAddress = ['ApartmentNumber', 'City', 'HouseNumber', 'Postcode', 'Street'].every((key) => {
            return beneficiary[`address${key}`] !== beneficiary[`mailing${key}`]
          })

          commit(types.ADD_BENEFICIARY, { beneficiary: { ...beneficiary, isOtherAddress } })
        })

        dispatch('saveBeneficiaryToStorage')
      },
    })
  },

  async getStorageBeneficiaries ({ commit, rootState, dispatch }, primaryBeneficiary) {
    if (primaryBeneficiary) {
      commit(types.ADD_BENEFICIARY, { beneficiary: primaryBeneficiary })
    }

    if (!rootState.user.isLogged) {
      const items = await expiringStorage.get('cart-beneficiaries', [])

      items.forEach(beneficiary => commit(types.ADD_BENEFICIARY, { beneficiary }))
    }

    if (primaryBeneficiary) {
      dispatch('saveBeneficiaryToStorage')
    }
  },
}
